import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { LearnedPresetKind } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import { areParcelRateInputsEqual } from "@redotech/redo-fulfillment-parcel-common/parcel";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";

export function shouldRecalculateRatesForUpdate(
  update: FulfillmentGroupUpdate,
  originalGroup: FulfillmentGroupData,
) {
  const parcelRateInputsChanged =
    update.selectedParcels != null &&
    !areParcelRateInputsEqual(
      originalGroup.selectedParcels,
      update.selectedParcels,
    );

  return Boolean(
    parcelRateInputsChanged ||
    update.signatureRequired != null ||
    update.adultSignatureRequired != null ||
    update.customsDeclarations ||
    update.contentsType ||
    update.contentsExplanation ||
    update.nonDeliveryOption ||
    update.uspsExemptionCode ||
    update.taxIdentifierIds ||
    update.customerTaxIds ||
    update.itn ||
    update.ddp != null ||
    update.hazmat !== undefined ||
    update.dryIceWeight !== undefined ||
    update.saturdayDelivery !== undefined ||
    update.specialRateEligibility !== undefined ||
    update.assignedFulfillmentService ||
    update.shipmentPaymentOption !== undefined ||
    update.directDeliveryOnly !== undefined ||
    update.carbonNeutral !== undefined ||
    update.canadaPostOption !== undefined ||
    update.dhlEcommerceLabelOptions !== undefined ||
    update.additionalCustomsComments !== undefined ||
    update.overrideResidentialAddress !== undefined,
  );
}

export function getOptimisticUpdates(
  originalData: FulfillmentGroupData[],
  updates: FulfillmentGroupUpdate[],
) {
  const newData = [...originalData];
  for (const { fulfillmentGroupId, ...rest } of updates) {
    const index = newData.findIndex((row) => row._id === fulfillmentGroupId);
    if (index < 0) {
      continue;
    }
    const originalGroup = newData[index];
    if (!originalGroup) {
      continue;
    }
    const fulfillmentGroupUpdate = { fulfillmentGroupId, ...rest };
    const update: Partial<FulfillmentGroupData> = { ...rest };
    if (rest.fulfillmentDate !== undefined) {
      update.marketplaceFulfillmentDelayCriteria = null;
    }
    if (
      shouldRecalculateRatesForUpdate(fulfillmentGroupUpdate, originalGroup)
    ) {
      update.ratesPendingSince = new Date();
    }
    // Picking a personalized insert kicks off async generation; show the
    // generating state immediately (cleared when "No personalized insert").
    if (rest.personalizedSlipTemplateId !== undefined) {
      update.personalizedSlipGeneratingSince = rest.personalizedSlipTemplateId
        ? new Date()
        : null;
    }
    newData[index] = { ...originalGroup, ...update };
    if (rest.overrideDeliveryMethod) {
      newData[index].deliveryMethod = rest.overrideDeliveryMethod;
    }
    if (update.ratesPendingSince || rest.selectedShippingRate !== undefined) {
      newData[index].buyLabelErrors = [];
    }
    if (rest.displayWeightGrams && newData?.[index]?.usedLearning != null) {
      newData[index].usedLearning[LearnedPresetKind.WEIGHT] = false;
    }
    if (rest.selectedParcels && newData?.[index]?.usedLearning != null) {
      newData[index].usedLearning[LearnedPresetKind.PARCEL] = false;
    }
    const weightOverrideChanged = rest.selectedParcels?.some(
      (parcel) => parcel.parcelAndContentWeight !== undefined,
    );
    if (weightOverrideChanged && newData?.[index]?.usedLearning != null) {
      newData[index].usedLearning[LearnedPresetKind.WEIGHT] = false;
    }
    if (
      (rest.selectedShippingRate || rest.selectedShippingRate === null) &&
      newData?.[index]?.usedLearning != null
    ) {
      newData[index].usedLearning[LearnedPresetKind.CARRIER_SERVICE_LEVEL] =
        false;
    }
  }
  return { newData, updates };
}
